import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import confirm from '../../../../../../modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomDocumentsContext from '../providers/DataRoomDocuments/DataRoomDocuments.context';
import DataRoomSelectedDocumentsContext from '../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { getIsNodeFile, getIsNodeFolder } from 'utils';

const useDeleteFile = () => {
  const { t } = useTranslation();
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { setCurrentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);
  const { selectedLabel, removeAll } = useContext(
    DataRoomSelectedDocumentsContext,
  );

  const docDeleteRequest = useCallback(
    async (
      nodes: string[],
      setIsRequestPending: (val: boolean) => void,
      deleteFolder: boolean,
      shouldRemoveSelected?: boolean,
    ) => {
      try {
        setIsRequestPending(true);
        await (deleteFolder
          ? dataRoomApi.nodes.deleteDataRoomNode(nodes[0])
          : dataRoomApi.nodes.bulkDeleteNodes(nodes));
        popSuccess(t('successfullyDeletedFile', { count: nodes.length }));
        setCurrentlyDisplayedNodes?.((old) =>
          old.filter((item) => !nodes.includes(item._id)),
        );

        if (shouldRemoveSelected) removeAll();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsRequestPending(false);
      }
    },
    [popSuccess, t, setCurrentlyDisplayedNodes, removeAll, popServerError],
  );

  const onDocDelete = useCallback(
    async (
      docs: DataRoomNode[],
      setIsRequestPending: (val: boolean) => void,
      shouldRemoveSelected?: boolean,
    ) => {
      const nodes = docs.map((el) => el._id);
      const count = nodes.length;
      const isSingularFile = count === 1;
      const isDeleteFolder = isSingularFile && getIsNodeFolder(docs[0]);
      const type =
        selectedLabel === 'filesSelected'
          ? 'files'
          : selectedLabel === 'foldersSelected'
          ? 'folders'
          : 'items';

      if (
        await confirm(
          {
            title: t('deleteNode', {
              count,
              type,
              ...(isSingularFile && {
                node: getIsNodeFile(docs[0]) ? t('file') : t('folder'),
                name: docs[0].name,
              }),
            }),
            content: t('thisCantBeUndone'),
            type: 'delete',
            submitBtnText: isSingularFile
              ? t('yes')
              : t('deleteItems', { count, type }),
            cancelBtnText: t('cancel'),
          },
          true,
        )
      ) {
        docDeleteRequest(
          nodes,
          setIsRequestPending,
          isDeleteFolder,
          shouldRemoveSelected,
        );
      }
    },
    [docDeleteRequest, selectedLabel, t],
  );

  return onDocDelete;
};

export default useDeleteFile;
